import React, { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Mail, MapPin, Phone, Send } from 'lucide-react';
import Navbar from '@/components/landing/Navbar';
import Footer from '@/components/landing/Footer';
import SEO from '@/components/SEO';
import {
  GURUGRAM_MAPS_URL,
  SUPPORT_EMAIL,
  SUPPORT_EMAIL_MAILTO,
  SUPPORT_PHONE_DUMMY_DISPLAY,
  SUPPORT_PHONE_DUMMY_TEL
} from '@/config/links';

const APPS_SCRIPT_API_URL =
  'https://script.google.com/macros/s/AKfycbz0YlknMwC_v3HFAJvMra2TqpOBYVpzzee1BPkNvZTxMtlvtg-eZk5WZlfRRA4D4drFHg/exec';

// Minimum time (ms) a real user is expected to spend before submitting.
// Bots typically POST within a few hundred ms of the page loading.
const MIN_SUBMIT_ELAPSED_MS = 2000;

const ContactUs = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    // Honeypot. Named so browser autofill / password managers ignore it
    // (anything like "website", "url", "company" gets autofilled silently).
    // Bots that blindly fill every input will still populate it — and reveal
    // themselves.
    hp_check: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });
  const formLoadedAtRef = useRef(Date.now());

  useEffect(() => {
    formLoadedAtRef.current = Date.now();
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitStatus({ type: '', message: '' });

    const elapsedMs = Date.now() - formLoadedAtRef.current;

    // Client-side guards that mirror what the backend also checks.
    // They won't stop a determined attacker, but they add zero friction
    // for real users and catch the overwhelming majority of spam bots.
    if (formData.hp_check) {
      setSubmitStatus({
        type: 'error',
        message: 'Submission blocked. Please try again.'
      });
      return;
    }
    if (elapsedMs < MIN_SUBMIT_ELAPSED_MS) {
      setSubmitStatus({
        type: 'error',
        message: 'That was quick! Please take a moment to review and resubmit.'
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        name: formData.name,
        email: formData.email,
        subject: formData.subject,
        message: formData.message,
        // Sent to the backend as `website` so the Apps Script code
        // doesn't need to change when the frontend field is renamed
        // to dodge autofill.
        website: formData.hp_check,
        elapsedMs
      };

      // Sent as a CORS "simple" request (no custom headers) so the browser
      // does not fire an OPTIONS preflight — Google Apps Script Web Apps
      // don't respond to OPTIONS and would otherwise log a spurious 405.
      let response = null;
      try {
        response = await fetch(APPS_SCRIPT_API_URL, {
          method: 'POST',
          body: JSON.stringify(payload)
        });
      } catch (networkError) {
        response = null;
      }

      if (!response) {
        throw new Error('Unable to connect right now. Please try again in a moment.');
      }

      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
      }

      let responseData = null;
      try {
        responseData = await response.json();
      } catch (parseError) {
        responseData = null;
      }

      if (responseData?.success === false) {
        throw new Error(responseData.error || 'Submission failed.');
      }

      setSubmitStatus({
        type: 'success',
        message: 'Message sent successfully'
      });
      setFormData({ name: '', email: '', subject: '', message: '', hp_check: '' });
      formLoadedAtRef.current = Date.now();
    } catch (error) {
      setSubmitStatus({
        type: 'error',
        message:
          error?.message === 'Failed to fetch'
            ? 'Unable to connect right now. Please check your network and try again.'
            : error?.message && error.message !== 'Submission failed.'
              ? error.message
              : 'Unable to submit your message right now. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Contact Boxd Logistics"
        description="Reach out to Boxd Logistics for support, partnerships, or product inquiries. We typically respond within a few working hours."
        keywords="contact boxd logistics, shipping support, logistics partnership, customer support"
        canonical="https://boxdlogistics.in/contact"
      />
      <Navbar />

      <section className="relative overflow-hidden bg-gradient-to-br from-blue-50 via-white to-gray-50 pt-32 pb-16 px-4">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              'linear-gradient(rgba(59, 130, 246, 0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(59, 130, 246, 0.03) 1px, transparent 1px)',
            backgroundSize: '52px 52px'
          }}
        />
        <div className="absolute -top-12 -right-10 h-72 w-72 rounded-full bg-blue-200/30 blur-3xl" />
        <div className="absolute -bottom-16 -left-10 h-80 w-80 rounded-full bg-blue-100/40 blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <span className="inline-flex items-center rounded-full border border-blue-200 bg-white/80 px-4 py-1.5 text-sm font-semibold text-blue-900 shadow-sm">
              Contact & Support
            </span>
            <h1 className="mt-5 text-4xl md:text-6xl font-bold leading-tight text-gray-900">
              Let us make shipping{' '}
              <span className="bg-gradient-to-r from-blue-800 to-blue-950 bg-clip-text text-transparent">
                easier for you
              </span>
            </h1>
            <p className="mt-5 text-lg md:text-xl text-gray-600 max-w-3xl leading-relaxed">
              Have a question about pricing, onboarding, integrations, or delivery operations? Share your details and
              our team will get back to you quickly.
            </p>
          </div>
        </div>
      </section>

      <section className="px-4 pb-16">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-12 gap-8 items-start">
            <div className="lg:col-span-5 flex flex-col gap-6">
              <div className="rounded-3xl border border-gray-200 bg-white p-7 md:p-8 shadow-sm">
                <h2 className="text-2xl font-bold text-gray-900 mb-3">Talk to our team</h2>
                <p className="text-gray-600 leading-relaxed mb-6">
                  Reach us directly for quick assistance. We typically respond within a few working hours.
                </p>
                <div className="space-y-4">
                  <a
                    href={SUPPORT_EMAIL_MAILTO}
                    className="flex items-center gap-3 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-gray-700 hover:border-blue-300 hover:text-blue-900 transition-colors"
                  >
                    <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-blue-700 to-blue-800 text-white">
                      <Mail className="h-5 w-5" />
                    </span>
                    <span className="font-medium">{SUPPORT_EMAIL}</span>
                  </a>
                  <a
                    href={`tel:${SUPPORT_PHONE_DUMMY_TEL}`}
                    className="flex items-center gap-3 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-gray-700 hover:border-blue-300 hover:text-blue-900 transition-colors"
                  >
                    <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-green-500 to-emerald-600 text-white">
                      <Phone className="h-5 w-5" />
                    </span>
                    <span className="font-medium">{SUPPORT_PHONE_DUMMY_DISPLAY}</span>
                  </a>
                </div>
              </div>

              <div className="rounded-3xl border border-gray-200 bg-white p-7 md:p-8 shadow-sm">
                <h3 className="text-lg font-bold text-gray-900 mb-5">Our Address</h3>
                <a
                  href={GURUGRAM_MAPS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Open Gurugram office in Google Maps"
                  className="inline-flex items-center gap-2 rounded-xl bg-blue-50 px-3 py-2 text-sm font-semibold text-blue-900 hover:bg-blue-100 transition-colors mb-5"
                >
                  <MapPin className="h-4 w-4" />
                  Open in Maps
                </a>
                <div className="space-y-5 text-gray-600 leading-relaxed text-sm">
                  <p>
                    <span className="font-semibold text-gray-800">Current Address (Gurugram)</span>
                    <br />
                    Boxd Logistics
                    <br />
                    8th Floor, Magnum Tower,
                    <br />
                    Sector 58, Golf Course Extension Road,
                    <br />
                    Gurugram, Haryana 122011
                  </p>
                  <p>
                    <span className="font-semibold text-gray-800">Registered Office</span>
                    <br />
                    2 GHA 20 Jawahar Nagar
                    <br />
                    Jaipur, 302004
                    <br />
                    India
                  </p>
                </div>
              </div>
            </div>

            <div className="lg:col-span-7">
              <div className="rounded-3xl border border-blue-200 bg-gradient-to-br from-white to-blue-50/40 p-7 pb-9 md:p-8 md:pb-10 shadow-xl flex flex-col">
                <div className="w-full flex flex-col">
                  <h2 className="text-3xl font-bold text-gray-900 mb-2">Send us a message</h2>
                  <p className="text-gray-600 mb-6">
                    Tell us about your requirement and our team will get back with the right solution.
                  </p>
                  {submitStatus.message && (
                    <p
                      className={`mb-6 rounded-xl border px-4 py-3 text-sm ${
                        submitStatus.type === 'success'
                          ? 'border-green-200 bg-green-50 text-green-700'
                          : 'border-red-200 bg-red-50 text-red-700'
                      }`}
                    >
                      {submitStatus.message}
                    </p>
                  )}
                  <form onSubmit={handleSubmit} className="space-y-5 flex flex-col" noValidate>
                    {/* Honeypot field.
                        - Named `hp_check` so autofill / password managers ignore it.
                        - data-lpignore + data-1p-ignore tell LastPass & 1Password to skip it.
                        - autoComplete="one-time-code" is the most reliably-ignored value
                          across Chrome/Safari/Firefox for plain text inputs.
                        - Positioned off-screen (not display:none) so bots still see it.
                        - tabIndex=-1 + aria-hidden keep it invisible to keyboard & AT users. */}
                    <div
                      aria-hidden="true"
                      style={{
                        position: 'absolute',
                        left: '-10000px',
                        top: 'auto',
                        width: '1px',
                        height: '1px',
                        overflow: 'hidden'
                      }}
                    >
                      <label>
                        Leave this field empty
                        <input
                          type="text"
                          name="hp_check"
                          tabIndex={-1}
                          autoComplete="one-time-code"
                          data-lpignore="true"
                          data-1p-ignore="true"
                          data-form-type="other"
                          value={formData.hp_check}
                          onChange={handleChange}
                        />
                      </label>
                    </div>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Your Name</label>
                        <Input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          className="w-full border-2 border-gray-200 focus:border-blue-700 rounded-xl px-4 py-3 bg-white"
                          placeholder="Enter your name"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Your Email</label>
                        <Input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="w-full border-2 border-gray-200 focus:border-blue-700 rounded-xl px-4 py-3 bg-white"
                          placeholder="Enter your email"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Subject</label>
                      <Input
                        type="text"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        className="w-full border-2 border-gray-200 focus:border-blue-700 rounded-xl px-4 py-4 bg-white"
                        placeholder="Enter subject"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Message</label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows="8"
                        className="w-full border-2 border-gray-200 focus:border-blue-700 rounded-xl px-4 py-3 resize-none bg-white"
                        placeholder="Your message..."
                      ></textarea>
                    </div>
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-gradient-to-r from-blue-800 to-blue-900 hover:from-blue-900 hover:to-blue-950 text-white py-3.5 text-lg font-semibold rounded-xl disabled:opacity-80"
                    >
                      <Send className="w-5 h-5 mr-2" />
                      {isSubmitting ? 'Sending...' : 'Send Message'}
                    </Button>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactUs;
